// @ts-strict-ignore

import {DATASET_MIXPANEL} from 'common/report/constants';
import {DisplayOptions} from 'common/report/insights/util/types';
import {ChartPlotStyle, ChartType, InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {insightsFilterAttrsFromPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {Section, TimeSection} from 'common/report/insights/models/section';
import {hasCohortsOverTimeMetric, hasRetentionMetric, hasXauMetric} from 'common/report/insights/models/utils';
import {Segmentation} from 'common/types/reports/query';
import {getSegmentValueFromSegmentation} from 'common/query-builder/query-block/segment-menu/types';

import {DEFAULT_STEPS_TABLE_COLUMNS} from '../visualization/constants';
import {GroupClause} from '../models/group-clause';
import {getBookmarkTimeClauseForDateRange, getDateRange} from './time';
import {getSegmentSectionsOverride, getSelectedSegments} from './segments';

function getDisplayOptionOverridesForChartType(chartType: ChartType): Pick<DisplayOptions, `chartType` | `plotStyle`> {
  switch (chartType) {
    case ChartType.FunnelSteps:
      return {
        chartType: ChartType.FunnelSteps,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.RetentionCurve:
      return {
        chartType: ChartType.RetentionCurve,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Bar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedBar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Line:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedLine:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Column:
      return {
        chartType: ChartType.Column,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedColumn:
      return {
        chartType: ChartType.Column,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Pie:
      return {
        chartType: ChartType.Pie,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Table:
      return {
        chartType: ChartType.Table,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Metric:
      return {
        chartType: ChartType.Metric,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.FunnelTopPaths:
      return {
        chartType: ChartType.FunnelTopPaths,
        plotStyle: ChartPlotStyle.Standard,
      };
    default: {
      throw new Error(`unknown chart type "${chartType}"`);
    }
  }
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithChartType(
  bookmarkParams: InsightsBookmarkParams,
  chartTypeOverride: ChartType,
): InsightsBookmarkParams {
  const {displayOptions} = bookmarkParams;
  const {funnelStepsSelectedTableColumns, ...restDisplayOptions} = displayOptions;
  return {
    ...bookmarkParams,
    displayOptions: {
      ...restDisplayOptions,
      ...getDisplayOptionOverridesForChartType(chartTypeOverride),
      ...(chartTypeOverride === ChartType.FunnelSteps
        ? {funnelStepsSelectedTableColumns: funnelStepsSelectedTableColumns || DEFAULT_STEPS_TABLE_COLUMNS}
        : {}),
    },
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithDateRange(
  bookmarkParams: InsightsBookmarkParams,
  dateRangeOverride: DateRange,
  timeUnitOverride?: TimeUnit,
): InsightsBookmarkParams {
  const currentTimeSection = Section.deserialize(TimeSection.TYPE, bookmarkParams.sections?.time);
  const currentDateRange = getDateRange(currentTimeSection.clauses);

  if (currentDateRange.equals(dateRangeOverride)) {
    return bookmarkParams;
  }

  const newTimeClause = getBookmarkTimeClauseForDateRange(dateRangeOverride);
  if (timeUnitOverride) {
    newTimeClause.unit = timeUnitOverride;
  }
  const subDayTimeUnits: TimeUnit[] = [TimeUnit.Hour, TimeUnit.Minute];
  // Supported XAU metric units are Day, Week, Month (see XAU_MATH_TYPES).
  // Retention and Cohorts Over Time metrics don't support smaller units than Day for perf reasons.
  if (
    subDayTimeUnits.includes(newTimeClause.unit) &&
    (hasRetentionMetric(bookmarkParams) || hasXauMetric(bookmarkParams) || hasCohortsOverTimeMetric(bookmarkParams))
  ) {
    newTimeClause.unit = TimeUnit.Day;
  }
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      time: [newTimeClause],
    },
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithAdditionalFilters(
  bookmarkParams: InsightsBookmarkParams,
  filters: PropertyFilter[] | null,
): InsightsBookmarkParams {
  const insightsFilters: TODO[] = (filters || []).map((propertyFilter) => {
    return insightsFilterAttrsFromPropertyFilter({dataset: DATASET_MIXPANEL, ...propertyFilter});
  });

  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      filter: [...bookmarkParams.sections.filter, ...insightsFilters],
    },
  };
}

export function getBookmarkParamsWithAdditionalBreakdowns(
  bookmarkParams: InsightsBookmarkParams,
  breakdowns: Segmentation[],
) {
  if (!breakdowns || breakdowns.length === 0) {
    return bookmarkParams;
  }

  const currentBreakdowns = getSelectedSegments(bookmarkParams.sections.group.map((grp) => new GroupClause(grp)));
  // The extra breakdowns are the innermost ones (a.k.a. the last ones)
  const {group, cohorts} = getSegmentSectionsOverride([
    ...currentBreakdowns,
    ...breakdowns.map(getSegmentValueFromSegmentation),
  ]);
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      group,
      cohorts,
    },
  };
}
